import { GUIA_LABELS } from '@utils/guiaLabels.js'
import { useEffect, useMemo, useRef, useState } from 'react'
import { getTopicosPorModulo, type NovaAnotacao } from '@hooks/useDiario.js'
import type { ContatoItem } from '@hooks/useContatos.js'
import type { DiarioClima } from '@services/db.js'
import { DIARIO_CLIMAS, MODULOS } from '@utils/constants.js'
import {
  ACCEPT_MIDIA_CANTEIRO,
  isVideo,
  textoErroSalvarMidia,
  textoTriagemMidia,
  triagemMidiaCanteiro,
} from '@utils/midia.js'
import { Card } from '@components/primitives/Card.js'
import { Button } from '@components/primitives/Button.js'
import { NavIcon } from '@components/layout/NavIcons.js'

interface Props {
  onSalvar: (input: NovaAnotacao) => Promise<void>
  equipe?: ContatoItem[]
  /** Abre já com o seletor de foto (atalho "Foto" do painel). */
  autoFoto?: boolean
}

interface FotoLocal {
  file: File
  url: string
}

export function DiarioEntradaRapida({ onSalvar, equipe = [], autoFoto = false }: Props) {
  const [texto, setTexto] = useState('')
  const [moduloId, setModuloId] = useState('')
  const [topicoId, setTopicoId] = useState('')
  const [vinculoAberto, setVinculoAberto] = useState(false)
  const [clima, setClima] = useState<DiarioClima | ''>('')
  const [equipeIds, setEquipeIds] = useState<number[]>([])
  const [fotos, setFotos] = useState<FotoLocal[]>([])
  const [avisoMidia, setAvisoMidia] = useState('')
  const [salvando, setSalvando] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const autoAberto = useRef(false)

  const topicos = moduloId ? getTopicosPorModulo(moduloId) : []
  const equipeVisivel = useMemo(() => equipe.filter((c) => c.papel !== 'fornecedor'), [equipe])

  useEffect(() => {
    if (autoFoto && !autoAberto.current) {
      autoAberto.current = true
      fileRef.current?.click()
    }
  }, [autoFoto])

  useEffect(() => {
    return () => {
      for (const f of fotos) URL.revokeObjectURL(f.url)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function adicionarArquivos(list: FileList | null) {
    if (!list) return
    const triagem = triagemMidiaCanteiro(Array.from(list), 8 - fotos.length)
    const novos = triagem.aceitos.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }))
    setFotos((prev) => [...prev, ...novos])
    setAvisoMidia(textoTriagemMidia(triagem))
    if (fileRef.current) fileRef.current.value = ''
  }

  function removerFoto(url: string) {
    setFotos((prev) => {
      const alvo = prev.find((f) => f.url === url)
      if (alvo) URL.revokeObjectURL(alvo.url)
      return prev.filter((f) => f.url !== url)
    })
    setAvisoMidia('')
  }

  const podeSalvar = Boolean(texto.trim()) || fotos.length > 0

  async function salvar() {
    if (!podeSalvar) return
    setSalvando(true)
    setAvisoMidia('')
    try {
      await onSalvar({
        texto,
        topicoId: topicoId || undefined,
        clima: clima || undefined,
        equipeIds: equipeIds.length ? equipeIds : undefined,
        fotos: fotos.length ? fotos.map((f) => f.file) : undefined,
      })
    } catch (err) {
      setAvisoMidia(textoErroSalvarMidia(err))
      setSalvando(false)
      return
    }
    for (const f of fotos) URL.revokeObjectURL(f.url)
    setTexto('')
    setModuloId('')
    setTopicoId('')
    setVinculoAberto(false)
    setClima('')
    setEquipeIds([])
    setFotos([])
    setSalvando(false)
  }

  return (
    <Card surface='raised' className='cf3-diario-form'>
      <div className='cf3-diario-form__head'>
        <p className='cf3-overline cf3-diario-form__title'>Hoje no canteiro</p>
        {!vinculoAberto ? (
          <button
            type='button'
            className='cf3-diario-vinculo__btn cf3-diario-vinculo__btn--head'
            onClick={() => setVinculoAberto(true)}
          >
            Vincular etapa
          </button>
        ) : (
          <button
            type='button'
            className='cf3-diario-vinculo__btn cf3-diario-vinculo__btn--head'
            onClick={() => {
              setModuloId('')
              setTopicoId('')
              setVinculoAberto(false)
            }}
          >
            Cancelar vínculo
          </button>
        )}
      </div>

      <textarea
        className='cf3-diario-form__input'
        rows={3}
        placeholder='O que aconteceu hoje? Serviço feito, combinados, entregas, problemas…'
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        aria-label='Texto da anotação'
      />

      <div className='cf3-diario-extra'>
        <div className='cf3-diario-fotos'>
          {fotos.map((f) => (
            <div key={f.url} className='cf3-diario-foto'>
              {isVideo(f.file) ? (
                <video src={f.url} muted playsInline preload='metadata' />
              ) : (
                <img src={f.url} alt='' />
              )}
              <button
                type='button'
                className='cf3-diario-foto__remove'
                aria-label='Remover mídia'
                onClick={() => removerFoto(f.url)}
              >
                ×
              </button>
            </div>
          ))}
          {fotos.length < 8 && (
            <label className='cf3-diario-foto cf3-diario-foto--add cf3-diario-foto--add-wide'>
              <NavIcon icon='camera' size={20} />
              <span>{fotos.length === 0 ? 'Foto ou vídeo' : 'Mais'}</span>
              <input
                ref={fileRef}
                type='file'
                accept={ACCEPT_MIDIA_CANTEIRO}
                multiple
                aria-label='Adicionar foto ou vídeo da obra'
                onChange={(e) => adicionarArquivos(e.target.files)}
              />
            </label>
          )}
        </div>
        {avisoMidia && (
          <p className='cf3-gestao-aviso' role='status'>
            {avisoMidia}
          </p>
        )}

        <div className='cf3-chip-row' role='radiogroup' aria-label='Clima do dia'>
          {DIARIO_CLIMAS.map((c) => {
            const value = c.value as DiarioClima
            return (
              <button
                key={value}
                type='button'
                role='radio'
                aria-checked={clima === value}
                className={`cf3-chip${clima === value ? ' cf3-chip--active' : ''}`}
                onClick={() => setClima((atual) => (atual === value ? '' : value))}
              >
                {c.label}
              </button>
            )
          })}
        </div>

        {equipeVisivel.length > 0 && (
          <div className='cf3-chip-row' role='group' aria-label='Quem trabalhou hoje'>
            {equipeVisivel.map((c) => {
              const on = equipeIds.includes(c.id)
              return (
                <button
                  key={c.id}
                  type='button'
                  aria-pressed={on}
                  className={`cf3-chip${on ? ' cf3-chip--active' : ''}`}
                  onClick={() =>
                    setEquipeIds((prev) =>
                      on ? prev.filter((id) => id !== c.id) : [...prev, c.id],
                    )
                  }
                >
                  {c.nome.split(' ')[0]}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {vinculoAberto && (
        <>
          <select
            className='cf-input cf3-diario-form__field'
            value={moduloId}
            onChange={(e) => {
              setModuloId(e.target.value)
              setTopicoId('')
            }}
            aria-label='Módulo'
          >
            <option value=''>Sem vínculo</option>
            {MODULOS.map((m) => (
              <option key={m.id} value={m.id}>
                {m.titulo}
              </option>
            ))}
          </select>
          {moduloId && topicos.length > 0 && (
            <select
              className='cf-input cf3-diario-form__field'
              value={topicoId}
              onChange={(e) => setTopicoId(e.target.value)}
              aria-label={GUIA_LABELS.etapa}
            >
              <option value=''>{GUIA_LABELS.etapa} (opcional)</option>
              {topicos.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          )}
        </>
      )}

      <Button onClick={salvar} loading={salvando} disabled={!podeSalvar}>
        {fotos.length > 0 && !texto.trim()
          ? `Salvar ${fotos.length} arquivo${fotos.length > 1 ? 's' : ''}`
          : 'Salvar anotação'}
      </Button>
    </Card>
  )
}
